import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment';
import { Project } from '../../lead-generation/shared/models/Project';
import { Store } from '@ngrx/store';
import { IState } from '../../shared/models/state';
import { getMetaDataSelector, getOverallMetaDataSelector, getProjectsSelector } from '../reports.reducer';
import { ReportMetaData, ReportType } from '../report';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportsService } from '../reports.service';
import { saveAs as importedSaveAs } from 'file-saver';
import { DATE_FORMAT } from '../../shared/utils/dateUtils';
import { NotificationService } from '../../shared/notification.service';
import { markFormAsTouched } from '../../lead-generation/shared/reactive-forms/forEachElementInForm';
import { AmplitudeService } from '../../shared/services/amplitude.service';
import { getUserDetailsSelector } from '../../shared/user/user.reducer';

@Component({
  selector: 'cc-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  public reportsForm: FormGroup;
  public overallReportsForm: FormGroup;
  public reportMetaData: ReportMetaData;
  public overallReportMetaData: ReportMetaData;
  public projects: Project[];
  public reportTypes: ReportType[];
  public inProgress = false;
  private successReportsCount: number = null;
  private failureReportsCount: number = null;
  public selectReportsCheckbox = new FormControl(false);
  public isAdministrativeUser = false;

  constructor(
    private fb: FormBuilder,
    private store: Store<IState>,
    private translate: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private reportsService: ReportsService,
    private notificationService: NotificationService,
    private amplitudeService: AmplitudeService
  ) {
    this.store.select(getMetaDataSelector).subscribe((reportMetaData: ReportMetaData) => {
      this.reportMetaData = reportMetaData;
      this.prepareReportTypes();
    });

    this.store.select(getOverallMetaDataSelector).subscribe((overallReportMetaData: ReportMetaData) => {
      this.overallReportMetaData = overallReportMetaData;
    });

    this.store.select(getUserDetailsSelector).subscribe(details => {
      this.isAdministrativeUser = details.administrative;
    });

    this.translate.onLangChange.subscribe(() => {
      this.prepareReportTypes();
    });

    const downloadToken = this.activatedRoute.snapshot.queryParamMap.get('download');
    if (downloadToken) {
      this.reportsService.downloadAsyncReports(downloadToken);
    }

    //TODO REMOVE AFTER VERSION 2.164 with com.crazycall.web.report.AsyncReportController
    const oldToken = this.activatedRoute.snapshot.queryParamMap.get('download-id');
    if (oldToken) {
      this.reportsService.downloadOldReport(oldToken);
    }
  }

  ngOnInit() {
    this.createEmptyForm();

    this.store.select(getProjectsSelector).subscribe((projects: Project[]) => {
      this.projects = projects.sort((a, b) => a.status.localeCompare(b.status));
    });
  }

  ngOnDestroy() {
    this.notificationService.dismiss();
  }

  changeReportTypes(event) {
    let selectedReportTypeValue = this.selectedReportTypes.value;
    let selectReportsCheckboxValue = false;

    if (event.checked) {
      selectedReportTypeValue.push(event.source.value);
    } else {
      selectedReportTypeValue = selectedReportTypeValue.filter(el => el !== event.source.value);
    }

    this.selectedReportTypes.setValue(selectedReportTypeValue);

    if (selectedReportTypeValue.length) {
      selectReportsCheckboxValue = this.reportTypes.length === selectedReportTypeValue.length ? true : null;
    }

    this.selectReportsCheckbox.setValue(selectReportsCheckboxValue);
  }

  changeSelectAllState(isSelect) {
    if (isSelect === null) {
      return;
    }
    const selectedReportTypes = isSelect ? this.reportTypes.map(el => el.type) : [];
    this.selectedReportTypes.setValue(selectedReportTypes);
  }

  isChecked(reportType) {
    return this.selectedReportTypes.value.includes(reportType);
  }

  private get selectedReportTypes() {
    return this.reportsForm.controls.reportTypes;
  }

  changeDateRange({ dateFrom, dateTo }, form) {
    form.get('dateFrom').setValue(moment(dateFrom).format(DATE_FORMAT));
    form.get('dateUntil').setValue(moment(dateTo).format(DATE_FORMAT));
  }

  selectAllProjects() {
    const projectIds = this.projects.map(project => project.id);
    this.reportsForm.controls.projectIds.setValue(projectIds);
  }

  unselectAllProjects() {
    this.reportsForm.controls.projectIds.setValue([]);
  }

  downloadProjectReportsAsync() {
    if (!this.reportsForm.valid) {
      markFormAsTouched(this.reportsForm);
      return;
    }

    this.doDownloadReportsAsync(this.reportsForm.getRawValue());
  }

  downloadOverallReport() {
    this.doDownloadReportsAsync(this.overallReportsForm.getRawValue());
  }

  private doDownloadReportsAsync(form) {
    this.inProgress = true;
    this.sendReportEventToAmplitude('Panel:Reports:Download: Start', form);
    this.reportsService.generateReportsAsync(form).subscribe(
      (reportTypesGenerated: [string]) => {
        this.inProgress = false;
        this.showReportMessage(reportTypesGenerated.length, form.reportTypes.length);
        this.sendReportEventToAmplitude('Panel:Reports:Download: Success', form);
      },
      () => {
        this.sendReportEventToAmplitude('Panel:Reports:Download: Error', form);
      }
    );
  }

  showReportMessage(reportTypesGenerated: number, reportTypesSelected: number) {
    this.notificationService.open(
      this.getReportGeneratedMessage(reportTypesGenerated, reportTypesSelected),
      {
        generatedReportsCount: reportTypesGenerated,
        selectedReportsCount: reportTypesSelected
      },
      this.notificationService.getDefaultCloseAction()
    );
  }

  downloadProjectReports() {
    if (!this.reportsForm.valid) {
      markFormAsTouched(this.reportsForm);
      return;
    }
    this.generateReports(this.reportsForm.getRawValue());
  }

  private generateReports(filters) {
    this.inProgress = true;
    this.reportsService.generateReports(filters).subscribe(response => {
      if (response.successReportsCount) {
        const blob = new Blob([response.blob], { type: 'application/zip, application/x-ms-excel' });
        importedSaveAs(blob, response.fileName);
      }
      this.successReportsCount = response.successReportsCount;
      this.failureReportsCount = filters.reportTypes.length - response.successReportsCount;
      this.inProgress = false;
    }, () => (this.inProgress = false));
  }

  isNoReportsGenerated() {
    return this.successReportsCount === 0;
  }

  isPartialReportsGenerated() {
    return this.failureReportsCount > 0 && this.successReportsCount > 0;
  }

  private createEmptyForm() {
    const today = moment().format(DATE_FORMAT);

    this.reportsForm = this.fb.group({
      dateFrom: [today, Validators.required],
      dateUntil: [today, Validators.required],
      projectIds: [[], Validators.required],
      reportTypes: [[], Validators.required],
      timezoneOffsetMinutes: moment().utcOffset()
    });

    this.overallReportsForm = this.fb.group({
      dateFrom: [today, Validators.required],
      dateUntil: [today, Validators.required],
      reportTypes: new Array(this.overallReportMetaData.types),
      timezoneOffsetMinutes: moment().utcOffset()
    });
  }

  private prepareReportTypes() {
    this.reportTypes = this.reportMetaData.types
      .map(el => this.prepareReportType(el))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  private prepareReportType(type: string) {
    return { type: type, title: this.translate.instant(`REPORT.${type}`) };
  }

  private getReportGeneratedMessage(reportTypesGenerated: number, reportTypesSelected: number): string {
    if (!reportTypesGenerated) {
      return 'REPORTS.NO_DATA';
    }
    if (reportTypesGenerated !== reportTypesSelected) {
      return 'REPORTS.PARTIAL_RESULT';
    }
    return 'REPORTS.ALL_DATA_GENERATED';
  }

  private sendReportEventToAmplitude(eventName, form) {
    this.amplitudeService.logEvent(eventName, {
      reportStartDate: form.dateFrom,
      reportEndDate: form.dateUntil,
      reportPeriod: moment(form.dateUntil).diff(moment(form.dateFrom), 'days') + 1,
      reportProjectsIds: form.projectIds || [],
      reportProjectsNumber: form.projectIds ? form.projectIds.length : 0,
      reportTypes: form.reportTypes,
      reportTypesNumber: form.reportTypes.length
    });
  }
}
